import { useCallback, useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, KeyRound, Trash2, Clock, PackagePlus, X, Check, RefreshCw, Download, SlidersHorizontal, Sparkles } from "lucide-react";

const resolveImg = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

const borderStyle = { borderColor: "var(--rs-border)" };
const inputCls =
  "w-full bg-transparent border px-4 py-3 text-sm text-bone placeholder:text-muted-bone outline-none";
const btnPrimary =
  "px-6 py-3 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition disabled:opacity-40";

const FIELD_LABELS = {
  account_number: "Account number",
  customer_id: "Customer ID",
  username: "Username / login email",
  password: "Password",
  api_key: "API key / token",
  custom_feed_url: "Custom feed URL",
  order_upload_url: "Order upload URL (XML sheet endpoint)",
  shipping_confirmations_url: "Shipping confirmations URL",
};

const FIELD_PLACEHOLDERS = {
  custom_feed_url: "https://www.honeysplace.com/download/…",
  order_upload_url: "sftp://host/orders/  or  https://…/orders (optional — ask the vendor)",
  shipping_confirmations_url: "sftp://host/confirmations/  or  https://…/tracking.xml (optional)",
};

const DEFAULT_FILTERS = {
  // Permissive defaults — merchant wants EVERY vendor SKU imported unless
  // explicitly filtered out. Only `in_stock_only` stays true so we never
  // list products vendors can't actually ship. Merchandisers can layer on
  // filters after seeing what a vendor stocks.
  in_stock_only: true,
  categories: [],
  brands_allow: [],
  brands_block: [],
  min_msrp: null,
  max_msrp: null,
  skip_closeouts: false,
  map_enforced_only: false,
  require_photo: false,
  keyword_blocklist: [],
  ai_curator: false,
};

const FilterPanel = ({
  facets, filters, setFilters, keywordText, setKeywordText,
  toggleArray, curatorPreview, curatorLoading, runCuratorPreview,
  eligible, countingEligible, onReset,
}) => {
  const brands = facets?.brands || [];
  const cats = facets?.categories || [];
  const [showAllBrands, setShowAllBrands] = useState(false);
  const visibleBrands = showAllBrands ? brands : brands.slice(0, 20);

  return (
    <div
      data-testid="catalog-filter-panel"
      className="mb-6 border p-6 space-y-6"
      style={{ ...borderStyle, background: "rgba(0,0,0,0.35)" }}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="overline flex items-center gap-2">
          <SlidersHorizontal size={13} /> Filter what enters the store
        </div>
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-widest">
          <span className="text-muted-bone">
            Eligible: <b style={{ color: "rgba(212,175,55,0.9)" }}>
              {countingEligible ? "…" : eligible?.toLocaleString() ?? "—"}
            </b> / {facets?.total?.toLocaleString() ?? "—"}
          </span>
          <button
            onClick={onReset}
            className="text-muted-bone hover:text-bone underline underline-offset-2"
            data-testid="catalog-filter-reset"
          >
            Reset
          </button>
        </div>
      </div>

      {!facets && <div className="text-xs text-muted-bone">Loading filters…</div>}

      {facets && (
        <>
          {/* Row 1 — quick toggles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ToggleChip label="In stock only" testid="f-instock"
              active={filters.in_stock_only}
              hint={`${facets.in_stock.toLocaleString()} of ${facets.total.toLocaleString()}`}
              onClick={() => setFilters((f) => ({ ...f, in_stock_only: !f.in_stock_only }))} />
            <ToggleChip label="Skip closeouts" testid="f-closeouts"
              active={filters.skip_closeouts}
              hint={`${facets.closeouts} in feed`}
              onClick={() => setFilters((f) => ({ ...f, skip_closeouts: !f.skip_closeouts }))} />
            <ToggleChip label="Photo required" testid="f-photo"
              active={filters.require_photo}
              hint={`${facets.with_photo.toLocaleString()} with photo`}
              onClick={() => setFilters((f) => ({ ...f, require_photo: !f.require_photo }))} />
            <ToggleChip label="MAP-enforced (premium)" testid="f-map"
              active={filters.map_enforced_only}
              hint={`${facets.map_enforced.toLocaleString()} MAP-only`}
              onClick={() => setFilters((f) => ({ ...f, map_enforced_only: !f.map_enforced_only }))} />
          </div>

          {/* Row 2 — categories */}
          <div>
            <div className="overline mb-2">Categories</div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <ToggleChip
                  key={c.category}
                  label={`${c.category} · ${c.count}`}
                  testid={`f-cat-${c.category}`}
                  active={filters.categories?.includes(c.category)}
                  onClick={() => toggleArray("categories", c.category)}
                />
              ))}
              <span className="text-[10px] text-muted-bone self-center ml-2">
                {filters.categories?.length ? "" : "(none = all)"}
              </span>
            </div>
          </div>

          {/* Row 3 — price band */}
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <label className="text-xs text-muted-bone uppercase tracking-widest">
              Min MSRP ($)
              <input
                type="number" min="0" step="1"
                data-testid="f-min-msrp"
                value={filters.min_msrp ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, min_msrp: e.target.value === "" ? null : Number(e.target.value) }))}
                className="mt-1 w-full bg-transparent border px-3 py-2 text-bone text-sm"
                style={borderStyle}
              />
            </label>
            <label className="text-xs text-muted-bone uppercase tracking-widest">
              Max MSRP ($) <span className="opacity-60">optional</span>
              <input
                type="number" min="0" step="1"
                data-testid="f-max-msrp"
                value={filters.max_msrp ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, max_msrp: e.target.value === "" ? null : Number(e.target.value) }))}
                className="mt-1 w-full bg-transparent border px-3 py-2 text-bone text-sm"
                style={borderStyle}
              />
            </label>
          </div>

          {/* Row 4 — brand allow / block */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Brands ({brands.length} in feed)</div>
              <div className="text-[10px] text-muted-bone">
                Click once = allowlist · Click again = blocklist · Third click = clear
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-1">
              {visibleBrands.map((b) => {
                const inAllow = filters.brands_allow?.includes(b.brand);
                const inBlock = filters.brands_block?.includes(b.brand);
                const state = inAllow ? "allow" : inBlock ? "block" : "none";
                const cycle = () => {
                  setFilters((f) => {
                    if (state === "none") {
                      return { ...f, brands_allow: [...(f.brands_allow || []), b.brand] };
                    } else if (state === "allow") {
                      return {
                        ...f,
                        brands_allow: (f.brands_allow || []).filter((x) => x !== b.brand),
                        brands_block: [...(f.brands_block || []), b.brand],
                      };
                    } else {
                      return { ...f, brands_block: (f.brands_block || []).filter((x) => x !== b.brand) };
                    }
                  });
                };
                const bg = state === "allow" ? "#2f6f4f" : state === "block" ? "#7a2c2c" : "transparent";
                const bd = state === "none" ? borderStyle : { borderColor: bg };
                return (
                  <button
                    key={b.brand}
                    onClick={cycle}
                    data-testid={`f-brand-${b.brand.replace(/\s+/g, "-")}`}
                    className="px-2.5 py-1 text-[11px] border text-bone"
                    style={{ ...bd, background: bg }}
                    title={state === "allow" ? "Allowlisted — click to blocklist" : state === "block" ? "Blocklisted — click to clear" : "Click to allowlist"}
                  >
                    {b.brand} <span className="opacity-60">· {b.count}</span>
                  </button>
                );
              })}
            </div>
            {brands.length > 20 && (
              <button
                onClick={() => setShowAllBrands((v) => !v)}
                className="mt-2 text-[11px] uppercase tracking-widest text-muted-bone hover:text-bone underline underline-offset-2"
              >
                {showAllBrands ? "Show fewer" : `Show all ${brands.length} brands`}
              </button>
            )}
          </div>

          {/* Row 5 — keyword blocklist */}
          <div>
            <label className="overline block mb-2">Keyword blocklist (comma-separated)</label>
            <input
              type="text"
              data-testid="f-keywords"
              placeholder="battery, energizer, party, gag, joke, prank, cleaning"
              value={keywordText}
              onChange={(e) => setKeywordText(e.target.value)}
              className="w-full bg-transparent border px-3 py-2 text-bone text-sm placeholder:text-muted-bone"
              style={borderStyle}
            />
          </div>

          {/* Row 6 — AI curator */}
          <div className="border-t pt-5" style={borderStyle}>
            <div className="flex flex-wrap items-start gap-4 justify-between">
              <div className="flex-1 min-w-[260px]">
                <div className="overline flex items-center gap-2 mb-1">
                  <Sparkles size={13} /> AI curator (Claude Haiku 4.5)
                </div>
                <p className="text-xs text-muted-bone leading-relaxed">
                  Reads each item&apos;s name + description and rejects anything that isn&apos;t
                  on-brand for &quot;vampire meets Santorini luxury.&quot; Adds ~$2–4 in tokens for the full feed
                  and ~5 minutes of wall time. Recommended for a lean, on-brand storefront.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="f-curator-preview"
                  onClick={runCuratorPreview}
                  disabled={curatorLoading}
                  className="px-4 py-2 text-[11px] uppercase tracking-widest text-muted-bone border hover:text-bone hover:bg-white/5 disabled:opacity-40"
                  style={borderStyle}
                >
                  {curatorLoading ? "Sampling…" : "Preview 20 items"}
                </button>
                <ToggleChip
                  label={filters.ai_curator ? "AI curator: ON" : "AI curator: OFF"}
                  testid="f-curator-toggle"
                  active={filters.ai_curator}
                  onClick={() => setFilters((f) => ({ ...f, ai_curator: !f.ai_curator }))}
                />
              </div>
            </div>

            {curatorPreview && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs" data-testid="curator-preview-results">
                <div>
                  <div className="overline mb-2" style={{ color: "#3ea27a" }}>
                    Kept ({curatorPreview.kept.length})
                  </div>
                  <ul className="space-y-1.5">
                    {curatorPreview.kept.map((k) => (
                      <li key={k.sku} className="text-muted-bone">
                        <b className="text-bone">{k.name.slice(0, 60)}</b> · ${k.msrp?.toFixed(2)}
                        <div className="opacity-70 italic">— {k.reason}</div>
                      </li>
                    ))}
                    {curatorPreview.kept.length === 0 && <li className="text-muted-bone italic">None in this sample</li>}
                  </ul>
                </div>
                <div>
                  <div className="overline mb-2" style={{ color: "#c05050" }}>
                    Skipped ({curatorPreview.skipped.length})
                  </div>
                  <ul className="space-y-1.5">
                    {curatorPreview.skipped.map((k) => (
                      <li key={k.sku} className="text-muted-bone">
                        <b className="text-bone">{k.name.slice(0, 60)}</b> · ${k.msrp?.toFixed(2)}
                        <div className="opacity-70 italic">— {k.reason}</div>
                      </li>
                    ))}
                    {curatorPreview.skipped.length === 0 && <li className="text-muted-bone italic">None in this sample</li>}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ToggleChip = ({ label, active, onClick, hint, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-2 text-[11px] uppercase tracking-widest text-left border ${active ? "text-bone" : "text-muted-bone hover:text-bone hover:bg-white/5"}`}
    style={active ? { background: "#2f6f4f", borderColor: "#2f6f4f" } : borderStyle}
  >
    {label}
    {hint && <div className="opacity-70 normal-case tracking-normal text-[10px] mt-0.5">{hint}</div>}
  </button>
);

const TestFeedButton = ({ supplierId, url }) => {
  const [state, setState] = useState({ loading: false, result: null });

  const test = async () => {
    if (!url || !url.trim()) {
      toast.error("Paste a feed URL first");
      return;
    }
    setState({ loading: true, result: null });
    try {
      const { data } = await api.post(`/admin/suppliers/${supplierId}/test-feed`, { url: url.trim() });
      setState({ loading: false, result: data });
      if (data.ok) {
        toast.success(`✓ ${data.count} items · ${data.format.toUpperCase()} feed reachable`);
      } else if (data.error_code === "feed_generating") {
        toast.info("Feed is still generating on the vendor's side — try again in a bit");
      } else if (data.error_code === "dns_failed") {
        toast.error("SFTP host not found — double-check the hostname in the URL");
      } else if (data.error_code === "sftp_auth_failed") {
        toast.error("SFTP login failed — check the username + password fields");
      } else if (data.error_code === "connection_refused") {
        toast.error("SFTP server refused the connection — wrong port or host down");
      } else if (data.error_code === "timeout") {
        toast.error("Feed timed out — vendor server may be slow, retry in a minute");
      } else {
        toast.error(data.error || "Feed URL is not reachable");
      }
    } catch (e) {
      setState({ loading: false, result: null });
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={test}
        disabled={state.loading}
        data-testid={`test-feed-${supplierId}`}
        className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-bone border hover:text-bone hover:bg-white/5 disabled:opacity-40 whitespace-nowrap"
        style={borderStyle}
        title="Hit the URL live and see if it parses"
      >
        {state.loading ? "Testing…" : "Test feed"}
      </button>
      {state.result && (
        <div className="text-[10px] text-right whitespace-nowrap"
             style={{ color: state.result.ok ? "#3ea27a" : state.result.error_code === "feed_generating" ? "#e0b850" : "#c05050" }}>
          {state.result.ok
            ? `✓ ${state.result.count} items`
            : state.result.error_code === "feed_generating"
            ? "⏳ generating"
            : "✗ failed"}
        </div>
      )}
    </div>
  );
};

const CatalogModal = ({ supplier, onClose }) => {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ total: 0, pages: 1, source: "curated" });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // debounced/applied search
  const [importing, setImporting] = useState(null);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [facets, setFacets] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [eligible, setEligible] = useState(null);
  const [countingEligible, setCountingEligible] = useState(false);
  const [keywordText, setKeywordText] = useState("");
  const [curatorPreview, setCuratorPreview] = useState(null);
  const [curatorLoading, setCuratorLoading] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/admin/suppliers/${supplier.id}/catalog`, {
        params: { page, page_size: 25, search: query },
      })
      .then((r) => {
        if (Array.isArray(r.data)) {
          setItems(r.data);
          setMeta({ total: r.data.length, pages: 1, source: "curated" });
        } else {
          setItems(r.data.items || []);
          setMeta({
            total: r.data.total || 0,
            pages: r.data.pages || 1,
            source: r.data.source || "curated",
          });
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load the catalogue");
        setItems([]);
      });
  }, [supplier.id, page, query]);

  useEffect(() => {
    load();
  }, [load]);

  // Load initial bulk import status once on open
  useEffect(() => {
    api
      .get(`/admin/suppliers/${supplier.id}/catalog/import-status`)
      .then((r) => setBulkStatus(r.data))
      .catch(() => {});
  }, [supplier.id]);

  // Load facets when the filter panel opens (only for live-feed vendors)
  useEffect(() => {
    if (!showFilters || facets || meta.source !== "live") return;
    api
      .get(`/admin/suppliers/${supplier.id}/catalog/facets`)
      .then((r) => setFacets(r.data))
      .catch(() => toast.error("Could not load filter facets"));
  }, [showFilters, facets, meta.source, supplier.id]);

  // Live-recount eligible items as filters change
  useEffect(() => {
    if (!showFilters || meta.source !== "live") return undefined;
    const payload = { ...filters, keyword_blocklist: keywordText.split(",").map((s) => s.trim()).filter(Boolean) };
    setCountingEligible(true);
    const t = setTimeout(() => {
      api
        .post(`/admin/suppliers/${supplier.id}/catalog/eligible-count`, payload)
        .then((r) => setEligible(r.data.eligible))
        .catch(() => {})
        .finally(() => setCountingEligible(false));
    }, 400);
    return () => clearTimeout(t);
  }, [filters, keywordText, showFilters, meta.source, supplier.id]);

  // Poll while a bulk import is running
  useEffect(() => {
    if (bulkStatus?.status !== "importing") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/suppliers/${supplier.id}/catalog/import-status`);
        setBulkStatus(data);
        if (data.status === "done") {
          toast.success(`Added ${data.added} · skipped ${data.skipped}${data.failed ? ` · failed ${data.failed}` : ""}`);
          load();
        } else if (data.status === "failed") {
          toast.error(`Import failed: ${data.error || "unknown error"}`);
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(t);
  }, [bulkStatus?.status, supplier.id, load]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const importItem = async (sku) => {
    setImporting(sku);
    try {
      const { data } = await api.post(`/admin/suppliers/${supplier.id}/catalog/import`, { sku });
      toast.success(`${data.name} added to your store`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(null);
    }
  };

  const importAll = async () => {
    const pending = (items || []).filter((i) => !i.imported);
    for (const i of pending) {
      // sequential to keep toasts readable
      // eslint-disable-next-line no-await-in-loop
      await importItem(i.sku);
    }
  };

  const importAllInStock = async () => {
    if (meta.source !== "live") {
      toast.error("Run Sync full catalog first — the live feed hasn't been pulled yet.");
      return;
    }
    const kwList = keywordText.split(",").map((s) => s.trim()).filter(Boolean);
    // Uncapped — the backend now downloads images inline, so imports take
    // longer but every eligible SKU comes through in a single run.
    const payload = { ...filters, keyword_blocklist: kwList, limit: 500000 };
    const summary = [
      filters.in_stock_only && "in-stock only",
      filters.skip_closeouts && "no closeouts",
      filters.require_photo && "with photo",
      filters.map_enforced_only && "MAP-enforced",
      filters.min_msrp && `MSRP ≥ $${filters.min_msrp}`,
      filters.categories?.length && `${filters.categories.length} categorie(s)`,
      filters.brands_allow?.length && `${filters.brands_allow.length} brand allowlist`,
      filters.brands_block?.length && `${filters.brands_block.length} brand blocklist`,
      kwList.length && `${kwList.length} keyword blocklist`,
      filters.ai_curator && "AI curator ON (~$2–4)",
    ].filter(Boolean).join(" · ");
    if (!window.confirm(
      `Import ~${eligible ?? "?"} item(s) from ${supplier.name}?\n\nFilters: ${summary || "none — every in-stock SKU"}\n\nEach product's real photo will be downloaded from the vendor at import time. Expect this to take a while for large catalogs (~1–2s per product). You can close this dialog — the import runs in the background and progress will keep updating on this page.`
    )) return;
    setBulkStarting(true);
    try {
      const { data } = await api.post(`/admin/suppliers/${supplier.id}/catalog/import-all`, payload);
      setBulkStatus({ status: "importing", eligible: data.eligible, added: 0, skipped: 0, failed: 0, ai_curator: data.ai_curator });
      toast.info(`Importing ${data.eligible.toLocaleString()} items in the background…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed to start");
    } finally {
      setBulkStarting(false);
    }
  };

  const runCuratorPreview = async () => {
    setCuratorLoading(true);
    setCuratorPreview(null);
    try {
      const { data } = await api.post(`/admin/suppliers/${supplier.id}/catalog/curator-preview`, {
        sample_size: 20,
        in_stock_only: filters.in_stock_only,
      });
      setCuratorPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Curator preview failed");
    } finally {
      setCuratorLoading(false);
    }
  };

  const toggleArray = (key, value) => {
    setFilters((f) => {
      const arr = f[key] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...f, [key]: next };
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(10,10,10,0.92)" }}>
      <div
        data-testid={`catalog-modal-${supplier.id}`}
        className="w-full max-w-4xl max-h-[88vh] overflow-y-auto border"
        style={{ ...borderStyle, background: "var(--rs-surface)" }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b sticky top-0 z-10" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
          <div>
            <div className="overline">
              Vendor catalogue{meta.source === "live" ? " · live feed" : " · curated picks"}
            </div>
            <div className="font-serif-display text-2xl text-bone">{supplier.name}</div>
            {meta.total > 0 && (
              <div data-testid="catalog-total" className="text-xs text-muted-bone mt-1">
                {meta.total.toLocaleString()} item{meta.total !== 1 ? "s" : ""}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <button
              data-testid="catalog-toggle-filters-btn"
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5"
              style={borderStyle}
              disabled={meta.source !== "live"}
              title={meta.source === "live" ? "Filter what enters your store" : "Run Sync full catalog first"}
            >
              {showFilters ? "Hide filters" : "Filters"}
              {eligible !== null && showFilters && (
                <span className="ml-1" style={{ color: "rgba(212,175,55,0.9)" }}>
                  · {countingEligible ? "…" : eligible.toLocaleString()}
                </span>
              )}
            </button>
            <button
              data-testid="catalog-import-all-instock-btn"
              onClick={importAllInStock}
              disabled={bulkStarting || bulkStatus?.status === "importing" || meta.source !== "live"}
              className="flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-40"
              style={{ background: "var(--rs-oxblood)" }}
              title={meta.source === "live" ? "Import filtered items from vendor's live feed" : "Run Sync full catalog first"}
            >
              <Download size={13} />
              {bulkStatus?.status === "importing"
                ? `Importing… ${bulkStatus.added || 0}/${bulkStatus.eligible || 0}`
                : eligible !== null && showFilters
                ? `Import ${eligible.toLocaleString()} matching`
                : "Import all in-stock"}
            </button>
            <button
              data-testid="catalog-import-all-btn"
              onClick={importAll}
              disabled={!items || items.length === 0 || items.every((i) => i.imported) || importing}
              className="px-5 py-2 text-xs uppercase tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5 disabled:opacity-40"
              style={borderStyle}
            >
              Add page only
            </button>
            <button data-testid="catalog-close-btn" onClick={onClose} className="text-muted-bone hover:text-bone" aria-label="Close">
              <X strokeWidth={1.5} size={18} />
            </button>
          </div>
        </div>
        <div className="p-6">
          {bulkStatus && bulkStatus.status !== "idle" && (
            <div
              data-testid="catalog-bulk-status"
              className="mb-6 px-4 py-3 border text-xs text-muted-bone flex flex-wrap gap-4 items-center"
              style={borderStyle}
            >
              {bulkStatus.status === "importing" && (
                <>
                  <RefreshCw size={12} className="animate-spin" />
                  <span className="uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.9)" }}>
                    Bulk import running
                  </span>
                  <span>Added <b className="text-bone">{bulkStatus.added || 0}</b></span>
                  <span>Skipped <b className="text-bone">{bulkStatus.skipped || 0}</b></span>
                  {!!bulkStatus.failed && <span>Failed <b className="text-bone">{bulkStatus.failed}</b></span>}
                  {!!bulkStatus.eligible && <span>Target <b className="text-bone">{bulkStatus.eligible.toLocaleString()}</b></span>}
                  {(bulkStatus.images_ok || bulkStatus.images_missing) ? (
                    <span>
                      · Images{" "}
                      <b className="text-bone">{bulkStatus.images_ok || 0}</b>
                      {" / "}
                      <b className="text-bone">{(bulkStatus.images_ok || 0) + (bulkStatus.images_missing || 0)}</b>
                    </span>
                  ) : null}
                </>
              )}
              {bulkStatus.status === "done" && (
                <>
                  <Check size={12} style={{ color: "#2f9f6f" }} />
                  <span className="uppercase tracking-widest text-bone">
                    Last bulk import
                  </span>
                  <span>Added <b className="text-bone">{bulkStatus.added || 0}</b></span>
                  <span>Skipped <b className="text-bone">{bulkStatus.skipped || 0}</b></span>
                  {!!bulkStatus.failed && <span>Failed <b className="text-bone">{bulkStatus.failed}</b></span>}
                  {(bulkStatus.images_ok || bulkStatus.images_missing) ? (
                    <span>
                      · Real images{" "}
                      <b className="text-bone">{bulkStatus.images_ok || 0}</b>
                      {bulkStatus.images_missing ? (
                        <>
                          {" · "}
                          <span style={{ color: "#d4af37" }}>
                            {bulkStatus.images_missing} placeholder{bulkStatus.images_missing !== 1 ? "s" : ""}
                          </span>
                        </>
                      ) : null}
                    </span>
                  ) : null}
                  <span>· in-stock only: {bulkStatus.in_stock_only ? "yes" : "no"}</span>
                </>
              )}
              {bulkStatus.status === "failed" && (
                <span style={{ color: "#c05050" }}>Bulk import failed — {bulkStatus.error}</span>
              )}
            </div>
          )}

          {showFilters && meta.source === "live" && (
            <FilterPanel
              facets={facets}
              filters={filters}
              setFilters={setFilters}
              keywordText={keywordText}
              setKeywordText={setKeywordText}
              toggleArray={toggleArray}
              curatorPreview={curatorPreview}
              curatorLoading={curatorLoading}
              runCuratorPreview={runCuratorPreview}
              eligible={eligible}
              countingEligible={countingEligible}
              onReset={() => { setFilters(DEFAULT_FILTERS); setKeywordText(""); setCuratorPreview(null); }}
            />
          )}
          <form onSubmit={applySearch} className="flex gap-2 mb-6">
            <input
              data-testid="catalog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or SKU…"
              className={inputCls}
              style={borderStyle}
            />
            <button
              type="submit"
              data-testid="catalog-search-btn"
              className="px-6 py-2 text-xs uppercase tracking-[0.22em] text-bone border hover:bg-white/5 shrink-0"
              style={{ borderColor: "rgba(245,242,235,0.25)" }}
            >
              Search
            </button>
          </form>
          {items === null ? (
            <div className="text-muted-bone">Loading catalogue…</div>
          ) : items.length === 0 ? (
            <div className="text-muted-bone">No items match{query ? ` “${query}”` : ""}.</div>
          ) : (
            <div className="space-y-3">
              {items.map((i) => (
                <div
                  key={i.sku}
                  data-testid={`catalog-item-${i.sku}`}
                  className="border p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                  style={borderStyle}
                >
                  <img src={resolveImg(i.image)} alt={i.name} className="w-14 h-14 object-cover border shrink-0" style={borderStyle} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-bone">{i.name}</div>
                    <div className="text-xs text-muted-bone">
                      {i.sku} · {i.category} · cost ${i.wholesale_cost.toFixed(2)} → retail ${i.msrp.toFixed(2)}
                      <span style={{ color: "rgba(212,175,55,0.9)" }}> · {i.margin_pct}% margin</span>
                      {i.stock_qty !== undefined && i.stock_qty !== null && (
                        <span style={{ color: i.stock_qty > 0 ? "#2f9f6f" : "#c05050" }}>
                          {" "}· {i.stock_qty > 0 ? `${i.stock_qty} in stock` : "out of stock"}
                        </span>
                      )}
                    </div>
                  </div>
                  {i.imported ? (
                    <span
                      data-testid={`catalog-imported-${i.sku}`}
                      className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-bone shrink-0"
                      style={{ background: "#2f6f4f" }}
                    >
                      <Check size={12} /> In store
                    </span>
                  ) : (
                    <button
                      data-testid={`catalog-import-${i.sku}`}
                      onClick={() => importItem(i.sku)}
                      disabled={importing === i.sku}
                      className="px-5 py-2 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-50 shrink-0"
                      style={{ background: "var(--rs-oxblood)" }}
                    >
                      {importing === i.sku ? "Adding…" : "Add to store"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {meta.pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t" style={borderStyle}>
              <button
                data-testid="catalog-prev-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-5 py-2 text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone border disabled:opacity-40"
                style={borderStyle}
              >
                ← Prev
              </button>
              <span data-testid="catalog-page-indicator" className="text-xs text-muted-bone uppercase tracking-widest">
                Page {page} of {meta.pages}
              </span>
              <button
                data-testid="catalog-next-page"
                onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                disabled={page >= meta.pages}
                className="px-5 py-2 text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone border disabled:opacity-40"
                style={borderStyle}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SupplierCard = ({ supplier, onSaved, onBrowse }) => {
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState(supplier.credentials_notes || "");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!supplier.connected);
  const [syncStatus, setSyncStatus] = useState(supplier.sync_status || null);
  const [syncing, setSyncing] = useState(false);

  // Poll sync status while a sync is running so the badge updates without a page refresh.
  useEffect(() => {
    if (!syncStatus || syncStatus.status !== "syncing") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/suppliers/${supplier.id}/catalog`, {
          params: { page: 1, page_size: 1 },
        });
        if (data.sync_status) {
          setSyncStatus(data.sync_status);
          if (data.sync_status.status !== "syncing") {
            setSyncing(false);
            if (data.sync_status.status === "synced") {
              toast.success(`${supplier.name} · ${data.sync_status.items?.toLocaleString() || 0} items synced`);
            } else if (data.sync_status.status === "failed") {
              toast.error(`${supplier.name} sync failed: ${data.sync_status.error || "unknown error"}`);
            }
          }
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [syncStatus, supplier.id, supplier.name]);

  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/admin/suppliers/${supplier.id}/catalog/sync`);
      setSyncStatus({ status: "syncing", started_at: new Date().toISOString() });
      toast.info(`${supplier.name} · pulling live catalog…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed to start");
      setSyncing(false);
    }
  };

  const save = async () => {
    const payload = Object.fromEntries(
      Object.entries(form).filter(([k, v]) => k !== "__focus" && v && v.trim()),
    );
    if (notes.trim()) payload.notes = notes.trim();
    if (Object.keys(payload).filter((k) => k !== "notes").length === 0) {
      toast.error("Enter at least one credential field");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/suppliers/${supplier.id}/credentials`, payload);
      toast.success(`${supplier.name} credentials saved`);
      setForm({});
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    await api.delete(`/admin/suppliers/${supplier.id}/credentials`);
    toast.success(`${supplier.name} credentials removed`);
    setEditing(true);
    onSaved();
  };

  return (
    <div
      data-testid={`supplier-card-${supplier.id}`}
      className="border p-6 flex flex-col gap-4"
      style={{ ...borderStyle, background: "var(--rs-surface)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-serif-display text-xl text-bone">{supplier.name}</div>
          <div className="flex gap-4 mt-1">
            <a
              href={supplier.website}
              target="_blank"
              rel="noreferrer"
              data-testid={`supplier-website-${supplier.id}`}
              className="text-xs text-muted-bone hover:text-bone flex items-center gap-1"
            >
              Website <ExternalLink size={11} />
            </a>
            <a
              href={supplier.portal_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`supplier-portal-${supplier.id}`}
              className="text-xs text-muted-bone hover:text-bone flex items-center gap-1"
            >
              Dealer portal <ExternalLink size={11} />
            </a>
          </div>
        </div>
        <span
          data-testid={`supplier-status-${supplier.id}`}
          className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone shrink-0"
          style={{ background: supplier.connected ? "#2f6f4f" : "#555" }}
        >
          {supplier.connected ? "Connected" : "Not connected"}
        </span>
      </div>

      <p className="text-xs text-muted-bone leading-relaxed">{supplier.notes}</p>
      <p className="text-xs leading-relaxed" style={{ color: "rgba(212,175,55,0.8)" }}>
        {supplier.credential_hint}
      </p>

      <button
        data-testid={`browse-catalog-${supplier.id}`}
        onClick={() => onBrowse(supplier)}
        className="flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.22em] text-bone hover:opacity-90 transition w-fit"
        style={{ background: "var(--rs-oxblood)" }}
      >
        <PackagePlus size={14} /> Browse catalogue — one-click add
      </button>

      {supplier.has_live_feed && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            data-testid={`sync-catalog-${supplier.id}`}
            onClick={runSync}
            disabled={syncing || syncStatus?.status === "syncing" || !supplier.connected}
            className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-bone border hover:bg-white/5 disabled:opacity-40"
            style={borderStyle}
            title={supplier.connected ? "Pull the vendor's full live catalogue" : "Connect credentials first"}
          >
            <RefreshCw size={12} className={syncStatus?.status === "syncing" ? "animate-spin" : ""} />
            {syncStatus?.status === "syncing" ? "Syncing…" : "Sync full catalog"}
          </button>
          {syncStatus?.status === "synced" && (
            <span
              data-testid={`sync-status-${supplier.id}`}
              className="text-[10px] uppercase tracking-widest"
              style={{ color: "rgba(212,175,55,0.9)" }}
            >
              Live feed · {syncStatus.items?.toLocaleString() || 0} items · {(syncStatus.synced_at || "").slice(0, 10)}
            </span>
          )}
          {syncStatus?.status === "failed" && !(supplier.id === "honeys_place" && ["no_custom_feed", "unreachable", "feed_generating"].includes(syncStatus?.error_code)) && (
            <span
              data-testid={`sync-error-${supplier.id}`}
              className="text-[10px] uppercase tracking-widest"
              style={{ color: "#c05050" }}
            >
              Failed · {(syncStatus.error || "").slice(0, 80)}
            </span>
          )}
          {syncStatus?.status === "syncing" && (
            <span className="text-[10px] uppercase tracking-widest text-muted-bone">
              Working… feed pulls can take 1–3 minutes
            </span>
          )}
        </div>
      )}

      {/* Persistent, actionable error banner for Honey's Place feed problems. */}
      {supplier.id === "honeys_place" && syncStatus?.status === "failed" &&
        ["no_custom_feed", "unreachable", "feed_generating"].includes(syncStatus?.error_code) && (
        <div
          data-testid={`sync-help-${supplier.id}`}
          className="border p-4 flex flex-col gap-3"
          style={syncStatus.error_code === "feed_generating"
            ? { borderColor: "#7a6a2c", background: "rgba(122,106,44,0.08)" }
            : { borderColor: "#7a2c2c", background: "rgba(122,44,44,0.08)" }}
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5" style={{ color: syncStatus.error_code === "feed_generating" ? "#e0b850" : "#c05050" }}>
              {syncStatus.error_code === "feed_generating" ? "⏳" : "⚠"}
            </span>
            <div className="text-xs text-bone leading-relaxed">
              <div className="uppercase tracking-widest mb-1" style={{ color: syncStatus.error_code === "feed_generating" ? "#e0b850" : "#e08080" }}>
                {syncStatus.error_code === "no_custom_feed" && "Custom feed URL required"}
                {syncStatus.error_code === "unreachable" && "Custom feed URL is not working"}
                {syncStatus.error_code === "feed_generating" && "Feed is still generating — hang tight"}
              </div>
              {syncStatus.error_code === "no_custom_feed" && (
                <>Honey&apos;s Place no longer publishes a public inventory feed. To sync the catalog, create a custom feed in their dashboard, then paste the download URL below.</>
              )}
              {syncStatus.error_code === "unreachable" && (
                <>The URL you saved doesn&apos;t return a valid product feed <span className="opacity-70">(server said: {(syncStatus.error || "").slice(0, 90)})</span>. Make sure you copied the <b>feed download URL</b> from <em>Data Feeds</em>, not the order-API endpoint.</>
              )}
              {syncStatus.error_code === "feed_generating" && (
                <>Honey&apos;s Place is still building your custom feed on their side. Per their docs this can take <b>up to a couple of hours</b> (usually much less). The nightly auto-sync will pick it up automatically the moment it&apos;s ready — no action needed. You can also click <b>Sync full catalog</b> again in a bit to check manually.</>
              )}
              {syncStatus.error_code !== "feed_generating" && (
                <ol className="list-decimal ml-5 mt-2 space-y-1 text-muted-bone">
                  <li>
                    Log into{" "}
                    <a href={supplier.portal_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 text-bone hover:opacity-80">
                      honeysplace.com
                    </a>{" "}
                    and open <b className="text-bone">My Account → Data Integration Services → Data Feeds</b>
                  </li>
                  <li>Create a feed with <b className="text-bone">Pricing</b>, <b className="text-bone">MAP</b>, and <b className="text-bone">Images</b> checked.</li>
                  <li>After saving, copy the <b className="text-bone">download URL</b> (usually ends in <code className="text-bone">.xml</code>, <code className="text-bone">.csv</code>, or <code className="text-bone">.json</code>).</li>
                  <li>Click <b className="text-bone">Add feed URL</b> below and paste it.</li>
                </ol>
              )}
            </div>
          </div>
          {syncStatus.error_code !== "feed_generating" && (
            <div className="flex gap-3">
              <button
                data-testid={`add-feed-url-${supplier.id}`}
                onClick={() => {
                  setEditing(true);
                  setForm((f) => ({ ...f, __focus: "custom_feed_url" }));
                }}
                className="px-4 py-2 text-[11px] uppercase tracking-widest text-bone"
                style={{ background: "var(--rs-oxblood)" }}
              >
                {syncStatus.error_code === "no_custom_feed" ? "Add feed URL" : "Fix feed URL"}
              </button>
              <a
                href={supplier.portal_url}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 text-[11px] uppercase tracking-widest text-muted-bone border hover:text-bone hover:bg-white/5 flex items-center gap-2"
                style={borderStyle}
              >
                Open Honey&apos;s dashboard <ExternalLink size={11} />
              </a>
            </div>
          )}
        </div>
      )}

      {supplier.connected && !editing ? (
        <div className="space-y-2">
          {Object.entries(supplier.credentials || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm">
              <span className="text-muted-bone">{FIELD_LABELS[k] || k}</span>
              <span className="text-bone font-mono text-xs">{v}</span>
            </div>
          ))}
          {supplier.credentials_updated_at && (
            <div className="text-[10px] text-muted-bone/70 uppercase tracking-widest">
              Updated {supplier.credentials_updated_at.slice(0, 10)}
            </div>
          )}
          <div className="flex gap-4 pt-2">
            <button
              data-testid={`supplier-edit-${supplier.id}`}
              onClick={() => setEditing(true)}
              className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone flex items-center gap-2"
            >
              <KeyRound size={13} /> Update credentials
            </button>
            <button
              data-testid={`supplier-disconnect-${supplier.id}`}
              onClick={disconnect}
              className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone flex items-center gap-2"
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {supplier.credential_fields.map((f) => (
            <div key={f} className="flex gap-2 items-start">
              <input
                data-testid={`supplier-${supplier.id}-${f}-input`}
                type={f === "password" || f === "api_key" ? "password" : "text"}
                value={form[f] || ""}
                autoFocus={form.__focus === f}
                ref={(el) => {
                  if (el && form.__focus === f) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => setForm((prev) => ({ ...prev, __focus: null })), 400);
                  }
                }}
                onChange={(e) => setForm((prev) => ({ ...prev, [f]: e.target.value }))}
                placeholder={
                  (FIELD_PLACEHOLDERS[f] || FIELD_LABELS[f]) +
                  (supplier.connected && supplier.credentials?.[f] ? ` (saved: ${supplier.credentials[f]})` : "")
                }
                className={`${inputCls} flex-1`}
                style={borderStyle}
              />
              {f === "custom_feed_url" &&
                ["honeys_place", "lifestyle", "xandria", "nalpac", "entrenue"].includes(supplier.id) && (
                <TestFeedButton supplierId={supplier.id} url={form[f] || supplier.credentials?.[f]} />
              )}
            </div>
          ))}
          <input
            data-testid={`supplier-${supplier.id}-notes-input`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (rep name, cutoff times…)"
            className={inputCls}
            style={borderStyle}
          />
          <div className="flex items-center gap-4">
            <button
              data-testid={`supplier-save-${supplier.id}`}
              onClick={save}
              disabled={saving}
              className={btnPrimary}
              style={{ background: "var(--rs-oxblood)" }}
            >
              {saving ? "Saving…" : "Save credentials"}
            </button>
            {supplier.connected && (
              <button
                data-testid={`supplier-cancel-${supplier.id}`}
                onClick={() => {
                  setEditing(false);
                  setForm({});
                }}
                className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminSuppliers = () => {
  const [suppliers, setSuppliers] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [delayInput, setDelayInput] = useState("");
  const [catalogFor, setCatalogFor] = useState(null);
  const [syncSchedule, setSyncSchedule] = useState(null);
  const [syncingNow, setSyncingNow] = useState(false);
  const [masterImport, setMasterImport] = useState(null);
  const [startingMasterImport, setStartingMasterImport] = useState(false);

  const load = useCallback(() => {
    api
      .get("/admin/suppliers")
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers([]));
    api
      .get("/admin/automation")
      .then((r) => {
        setAutomation(r.data);
        setDelayInput(String(r.data.auto_forward_delay_hours ?? 48));
      })
      .catch(() => {});
    api
      .get("/admin/vendors/sync-schedule")
      .then((r) => setSyncSchedule(r.data))
      .catch(() => {});
    api
      .get("/admin/vendors/import-all-connected/status")
      .then((r) => setMasterImport(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll the sync schedule while an ad-hoc sync or a backfill is in flight,
  // so the "Next run" line and progress badges stay truthful without a refresh.
  useEffect(() => {
    const backfillRunning = syncSchedule?.backfill?.status === "running";
    const anyVendorSyncing = (syncSchedule?.vendors || []).some(
      (v) => v.status === "syncing",
    );
    const masterRunning = masterImport?.status === "running";
    if (!backfillRunning && !anyVendorSyncing && !masterRunning) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/vendors/sync-schedule");
        setSyncSchedule(data);
      } catch (_) { /* keep polling */ }
      try {
        const { data } = await api.get("/admin/vendors/import-all-connected/status");
        setMasterImport(data);
      } catch (_) { /* keep polling */ }
    }, 8000);
    return () => clearInterval(t);
  }, [syncSchedule?.backfill?.status, syncSchedule?.vendors, masterImport?.status]);

  const runMasterImport = async () => {
    if (!window.confirm(
      "Import EVERY connected vendor's catalog into your Products list?\n\nThis walks Lifestyle, Eldorado, Honey's Place (and any others connected) one at a time, inserts every in-stock SKU as a new product, then automatically kicks the image backfill. Expect several hours for a full catalog — you can leave this page and come back.",
    )) return;
    setStartingMasterImport(true);
    try {
      await api.post("/admin/vendors/import-all-connected", { in_stock_only: true });
      toast.info("Master Import started — every connected vendor will import sequentially");
      const { data } = await api.get("/admin/vendors/import-all-connected/status");
      setMasterImport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Master Import failed to start");
    } finally {
      setStartingMasterImport(false);
    }
  };

  const runSyncNow = async () => {
    if (!window.confirm(
      "Refresh every connected vendor's catalog AND re-run the image backfill?\n\nThis pulls the latest inventory from Lifestyle, Eldorado, Honey's Place (and any others connected), then walks any products still missing a real photo. Runs in the background — expect 30 min – 2 hrs for a full cycle.",
    )) return;
    setSyncingNow(true);
    try {
      await api.post("/admin/vendors/sync-now");
      toast.info("Catalog sync + image backfill started");
      // Kick a status refresh immediately so the polling loop engages
      const { data } = await api.get("/admin/vendors/sync-schedule");
      setSyncSchedule(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync-now failed to start");
    } finally {
      setSyncingNow(false);
    }
  };

  const toggleAutoForward = async () => {
    const { data } = await api.patch("/admin/automation", {
      auto_forward_enabled: !automation.auto_forward_enabled,
    });
    setAutomation(data);
    toast.success(data.auto_forward_enabled ? "Auto-forwarding ON" : "Auto-forwarding OFF");
  };

  const saveDelay = async () => {
    const hours = parseInt(delayInput, 10);
    if (!hours || hours < 1 || hours > 336) {
      toast.error("Delay must be between 1 and 336 hours");
      return;
    }
    const { data } = await api.patch("/admin/automation", { auto_forward_delay_hours: hours });
    setAutomation(data);
    toast.success(`Orders will auto-forward ${hours}h after payment`);
  };

  const toggleNightlyCatalog = async () => {
    const { data } = await api.patch("/admin/automation", {
      catalog_sync_enabled: !automation.catalog_sync_enabled,
    });
    setAutomation(data);
    toast.success(data.catalog_sync_enabled ? "Nightly catalog sync ON" : "Nightly catalog sync OFF");
  };

  // Convert between a local hour (0–23 in the merchant's browser timezone) and
  // the stored UTC hour. Uses today's offset, so DST shifts will move the sync
  // ±1h twice a year — acceptable trade-off vs storing a full IANA tz name.
  const utcHourToLocalHour = (utcHour) => {
    if (utcHour == null || Number.isNaN(utcHour)) return null;
    const d = new Date();
    d.setUTCHours(utcHour, 0, 0, 0);
    return d.getHours();
  };
  const localHourToUtcHour = (localHour) => {
    const d = new Date();
    d.setHours(localHour, 0, 0, 0);
    return d.getUTCHours();
  };
  const formatHour = (hour) => {
    const d = new Date();
    d.setHours(hour, 0, 0, 0);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  };
  const localTzShort = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName")?.value || "";

  const changeSyncHour = async (newLocalHour) => {
    const utcHour = localHourToUtcHour(newLocalHour);
    try {
      const { data } = await api.patch("/admin/automation", { catalog_sync_hour_utc: utcHour });
      setAutomation(data);
      // Refresh the schedule so "Next run" reflects the new hour immediately.
      const { data: sched } = await api.get("/admin/vendors/sync-schedule");
      setSyncSchedule(sched);
      toast.success(`Nightly sync moved to ${formatHour(newLocalHour)} ${localTzShort}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update sync time");
    }
  };

  return (
    <div data-testid="admin-suppliers">
      <div className="overline mb-3">Drop-ship vendors</div>
      <h1 className="font-serif-display text-5xl text-bone mb-4">Vendors.</h1>
      <p className="text-muted-bone text-sm max-w-2xl mb-10 leading-relaxed">
        Store your wholesale account credentials here as each vendor finalises your
        application. Secrets are masked after saving. Once API credentials are in
        place, order forwarding can be switched from manual portal entry to
        automatic submission.
      </p>

      {/* Vendor sync scheduler — top of the page so it's the first thing an
          admin sees. Shows next auto-sync + last run + a one-click refresh
          that fires "catalog sync then image backfill" in one background job. */}
      {syncSchedule && (
        <div
          data-testid="vendor-sync-scheduler"
          className="border p-6 mb-6"
          style={{ ...borderStyle, background: "var(--rs-surface)" }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex-1 min-w-0">
              <div className="overline flex items-center gap-2 mb-3" style={{ color: "#d4af37" }}>
                <RefreshCw size={13} /> Auto-sync scheduler
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/70 mb-1">
                    Runs nightly at
                  </div>
                  <div className="flex items-center gap-2">
                    {syncSchedule.enabled ? (
                      <>
                        <select
                          data-testid="vendor-sync-hour-picker"
                          value={utcHourToLocalHour(syncSchedule.hour_utc) ?? ""}
                          onChange={(e) => changeSyncHour(parseInt(e.target.value, 10))}
                          className="bg-transparent border px-2 py-1 text-sm text-bone hover:text-bone focus:text-bone outline-none cursor-pointer"
                          style={{ borderColor: "rgba(212,175,55,0.4)" }}
                          title={`UTC ${String(syncSchedule.hour_utc).padStart(2, "0")}:00 · adjust to your local rhythm`}
                        >
                          {Array.from({ length: 24 }, (_, h) => (
                            <option key={h} value={h} style={{ background: "var(--rs-bg)" }}>
                              {formatHour(h)} {localTzShort}
                            </option>
                          ))}
                        </select>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-bone/70 font-mono">
                          {String(syncSchedule.hour_utc).padStart(2, "0")}:00 UTC
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-bone">Disabled</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/70 mb-1">
                    Next run
                  </div>
                  <div className="text-bone" data-testid="vendor-sync-next-run">
                    {syncSchedule.next_run_at ? (
                      <>
                        {new Date(syncSchedule.next_run_at).toLocaleString(undefined, {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit", timeZoneName: "short",
                        })}
                      </>
                    ) : (
                      <span className="text-muted-bone">—</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-muted-bone/70 mb-1">
                    Last run
                  </div>
                  <div className="text-bone" data-testid="vendor-sync-last-run">
                    {syncSchedule.last_run_at ? (
                      <>
                        {new Date(syncSchedule.last_run_at).toLocaleString(undefined, {
                          month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit",
                        })}
                        {syncSchedule.last_run_vendors?.length ? (
                          <span className="text-muted-bone">
                            {" · "}{syncSchedule.last_run_vendors.length} vendor{syncSchedule.last_run_vendors.length !== 1 ? "s" : ""}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted-bone">Never</span>
                    )}
                  </div>
                </div>
                {syncSchedule.backfill?.status === "running" && (
                  <div data-testid="vendor-sync-backfill-progress">
                    <div className="text-[10px] uppercase tracking-[0.24em] mb-1" style={{ color: "#d4af37" }}>
                      Backfill running
                    </div>
                    <div className="text-bone flex items-center gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ background: "#d4af37" }}
                      />
                      {syncSchedule.backfill.processed || 0}/{syncSchedule.backfill.total || 0}
                      <span className="text-muted-bone">
                        · +{syncSchedule.backfill.updated || 0} images
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {/* Per-vendor mini-row */}
              <div className="flex flex-wrap gap-2 mt-4">
                {syncSchedule.vendors
                  ?.filter((v) => v.configured)
                  .map((v) => (
                    <span
                      key={v.supplier_id}
                      data-testid={`vendor-sync-chip-${v.supplier_id}`}
                      className="text-[10px] uppercase tracking-[0.2em] border px-2 py-1 text-muted-bone flex items-center gap-2"
                      style={{
                        borderColor: v.status === "syncing"
                          ? "rgba(212,175,55,0.6)"
                          : v.error ? "#c05050" : "var(--rs-border)",
                      }}
                      title={v.error || `Cached: ${v.cache_count.toLocaleString()} SKUs`}
                    >
                      {v.status === "syncing" && (
                        <span
                          className="w-1.5 h-1.5 rounded-full animate-pulse"
                          style={{ background: "#d4af37" }}
                        />
                      )}
                      {v.supplier_name}
                      <span style={{ color: v.status === "syncing" ? "#d4af37" : "rgba(212,175,55,0.6)" }}>
                        {v.cache_count.toLocaleString()}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:items-end">
              <button
                data-testid="vendor-sync-now-btn"
                onClick={runSyncNow}
                disabled={syncingNow
                  || syncSchedule.backfill?.status === "running"
                  || (syncSchedule.vendors || []).some((v) => v.status === "syncing")}
                className="flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.24em] text-bone disabled:opacity-40"
                style={{ background: "var(--rs-oxblood)" }}
              >
                <RefreshCw
                  size={13}
                  className={
                    syncSchedule.backfill?.status === "running"
                    || (syncSchedule.vendors || []).some((v) => v.status === "syncing")
                      ? "animate-spin"
                      : ""
                  }
                />
                {syncingNow ? "Starting…" : "Refresh now"}
              </button>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-bone/70 max-w-[220px] lg:text-right">
                Pulls fresh feeds + backfills any missing product images
              </div>

              {/* ─── Master Import — one click, every vendor ─── */}
              <button
                data-testid="master-import-btn"
                onClick={runMasterImport}
                disabled={startingMasterImport || masterImport?.status === "running"}
                className="mt-3 flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.24em] border text-bone hover:bg-white/5 transition disabled:opacity-40"
                style={{ borderColor: "#d4af37" }}
              >
                <Download
                  size={13}
                  className={masterImport?.status === "running" ? "animate-pulse" : ""}
                />
                {masterImport?.status === "running"
                  ? masterImport.phase === "sync"
                    ? `Syncing feeds…`
                    : `Importing ${masterImport.vendors_done || 0}/${masterImport.vendors_total || "?"}…`
                  : startingMasterImport
                    ? "Starting…"
                    : "Import ALL vendors"}
              </button>
              {masterImport?.status === "running" && (
                <div
                  data-testid="master-import-progress"
                  className="text-[10px] uppercase tracking-[0.2em] text-muted-bone/70 max-w-[240px] lg:text-right leading-relaxed"
                >
                  Current: {masterImport.current_vendor || "—"}
                  <br />
                  Added: {(masterImport.added_total || 0).toLocaleString()}
                  {" · "}
                  Skipped: {(masterImport.skipped_total || 0).toLocaleString()}
                </div>
              )}
              {masterImport?.status === "done" && masterImport.finished_at && (
                <div
                  data-testid="master-import-done"
                  className="text-[10px] uppercase tracking-[0.2em] text-muted-bone/70 max-w-[240px] lg:text-right leading-relaxed"
                  style={{ color: "#d4af37" }}
                >
                  Last run: added {(masterImport.added_total || 0).toLocaleString()}
                  {" · "}
                  {masterImport.backfill_queued
                    ? `${masterImport.backfill_queued.toLocaleString()} images backfilling`
                    : "no images pending"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {automation && (
        <div
          data-testid="auto-forward-settings"
          className="border p-6 mb-10 flex flex-col sm:flex-row sm:items-center gap-6 flex-wrap"
          style={{ ...borderStyle, background: "var(--rs-surface)" }}
        >
          <div className="flex-1 min-w-[220px]">
            <div className="overline flex items-center gap-2 mb-2">
              <Clock size={13} /> Automatic order forwarding
            </div>
            <p className="text-xs text-muted-bone leading-relaxed">
              Paid orders are automatically processed for supplier forwarding after the
              delay below. Vendors with wired APIs get auto-submitted; the rest trigger a
              dispatch email with the shipping address and portal links.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                data-testid="auto-forward-delay-input"
                type="number"
                min="1"
                max="336"
                value={delayInput}
                onChange={(e) => setDelayInput(e.target.value)}
                className="w-20 bg-transparent border px-3 py-2 text-sm text-bone outline-none"
                style={borderStyle}
              />
              <span className="text-xs text-muted-bone uppercase tracking-widest">hours</span>
              <button
                data-testid="auto-forward-delay-save"
                onClick={saveDelay}
                className="text-xs uppercase tracking-widest text-muted-bone hover:text-bone underline underline-offset-4"
              >
                Save
              </button>
            </div>
            <button
              data-testid="auto-forward-toggle"
              onClick={toggleAutoForward}
              className={`px-4 py-2 text-[10px] uppercase tracking-widest ${automation.auto_forward_enabled ? "text-bone" : "text-muted-bone border"}`}
              style={automation.auto_forward_enabled ? { background: "#2f6f4f" } : borderStyle}
            >
              {automation.auto_forward_enabled ? "On" : "Off"}
            </button>
          </div>
        </div>
      )}

      {automation && (
        <div
          data-testid="nightly-catalog-settings"
          className="border p-6 mb-10 flex flex-col sm:flex-row sm:items-center gap-6 flex-wrap"
          style={{ ...borderStyle, background: "var(--rs-surface)" }}
        >
          <div className="flex-1 min-w-[220px]">
            <div className="overline flex items-center gap-2 mb-2">
              <RefreshCw size={13} /> Nightly catalog sync
            </div>
            <p className="text-xs text-muted-bone leading-relaxed">
              Every night at ~{String(automation.catalog_sync_hour_utc ?? 8).padStart(2, "0")}:00 UTC the store pulls the
              latest live catalogue from every connected vendor (Eldorado SFTP, Honey&apos;s XML feed,
              Williams Trading public feed). New SKUs appear automatically in{" "}
              <em>Browse catalogue</em>; existing products get fresh stock counts.
            </p>
          </div>
          <button
            data-testid="nightly-catalog-toggle"
            onClick={toggleNightlyCatalog}
            className={`px-4 py-2 text-[10px] uppercase tracking-widest ${automation.catalog_sync_enabled ? "text-bone" : "text-muted-bone border"}`}
            style={automation.catalog_sync_enabled ? { background: "#2f6f4f" } : borderStyle}
          >
            {automation.catalog_sync_enabled ? "On" : "Off"}
          </button>
        </div>
      )}

      {suppliers === null ? (
        <div className="text-muted-bone">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {suppliers.map((s) => (
            <SupplierCard key={s.id} supplier={s} onSaved={load} onBrowse={setCatalogFor} />
          ))}
        </div>
      )}

      {catalogFor && <CatalogModal supplier={catalogFor} onClose={() => setCatalogFor(null)} />}
    </div>
  );
};

export default AdminSuppliers;
